import { InputValidationRules } from 'vuetify';
import i18n from '@/i18n';
import { CountryInfo, creditCardType } from '@/features/common/types';
import { CarrierCodeType } from '@/features/shipment/types';
import { isAlphaNumeric, isNumeric } from './strUtils';
import { validateCCWithMessage } from './cardValidation';
import { safeListedCountryCodes } from '../raw';

export const emailRegEx = /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()\\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const charactersLimitRules = (limit: number): InputValidationRules => {
  return [
    (v: string): string | boolean => (v ? v.length <= limit
      || i18n.t('labels.charsLimitOverflow', { limit }) as string : true),
  ];
};

export const minCharactersRules = (limit: number): InputValidationRules => {
  return [
    (v: string): string | boolean => (v ? v.trim().length >= limit
      || i18n.t('shipment.validationErrors.minCharsLimit', { limit }) as string : true),
  ];
};

export const stripUnoCharFromPostalCode = (postalCode: string): string => {
  if (!postalCode) return '';
  return postalCode.replace(
    /[^A-Za-z0-9]/gm, '',
  );
};

export const isPostalCodeComplete = (
  postalCode: string,
  countryCode: string,
  countryInfo?: CountryInfo | null,
): boolean => {
  if (!postalCode || !countryCode) return false;

  if (countryInfo === null || countryInfo === undefined) {
    return false;
  }

  const { lengths } = countryInfo.postalInfo;

  if (!lengths || lengths.length === 0) return false;

  const stripped = stripUnoCharFromPostalCode(postalCode);
  const minLength = Math.min(...lengths);

  if (stripped.length < minLength) return false;

  if (countryCode === 'CA') {
    return /^[A-Za-z]/.test(stripped);
  }

  return true;
};

const isPostalCodeRequiredRule = (value: string, currentCountry: CountryInfo): boolean => {
  if (!currentCountry) return false;

  const isRequired = !!currentCountry.postalInfo.required;

  return isRequired ? !!value : true;
};

const isAlphaNumericPostalCode = (value: string, currentCountry: CountryInfo): boolean => {
  if (!currentCountry) return false;

  if (!currentCountry.postalInfo.isAlphaNumeric && value) {
    const regex = /^[0-9]+$/i;
    return regex.test(stripUnoCharFromPostalCode(value));
  }

  return true;
};

const isValidPostalCodeLength = (value: string, currentCountry: CountryInfo): boolean => {
  if (!currentCountry) return false;

  if (value && value.length > 15) return false;

  const { lengths, required } = currentCountry.postalInfo;
  if (!required) return true;

  if (!lengths?.length) return true;
  if (!currentCountry.required && !value) return true;

  const validatedString = stripUnoCharFromPostalCode(value);
  return !!lengths?.includes(validatedString.length);
};

const isValidCanadaPostalCode = (value: string, currentCountry: CountryInfo): boolean => {
  if (currentCountry && currentCountry.code === 'CA') {
    const regex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
    return !!value && regex.test(stripUnoCharFromPostalCode(value));
  }
  return true;
};

const isValidUsaPostalCode = (value: string, currentCountry: CountryInfo): boolean => {
  if (currentCountry && currentCountry.code === 'US') {
    return !!value && value !== '00000';
  }
  return true;
};

export const getPostalCodeRules = (currentCountry: CountryInfo): InputValidationRules => {
  return [
    (v: string): string | boolean => {
      return isPostalCodeRequiredRule(v, currentCountry) || i18n.t('labels.required') as string;
    },
    (v: string): string | boolean => {
      return isAlphaNumericPostalCode(v, currentCountry) || i18n.t('labels.postalCodeShouldOnlyNumbers') as string;
    },
    (v: string): string | boolean => {
      return isValidPostalCodeLength(v, currentCountry) || i18n.t('labels.invalidLength') as string;
    },
    (v: string): string | boolean => {
      return isValidCanadaPostalCode(v, currentCountry) || i18n.t('labels.invalidPostalCode') as string;
    },
    (v: string): string | boolean => {
      return isValidUsaPostalCode(v, currentCountry) || i18n.t('labels.invalidPostalCode') as string;
    },
  ];
};

export const isPostalCodeValid = (postalCode: string, countryInfo: CountryInfo): boolean => {
  if (!countryInfo) return false;

  const rules = getPostalCodeRules(countryInfo);
  return rules.every((rule) => {
    if (typeof rule === 'function') {
      const result = rule(postalCode);
      return result === true;
    }
    return false;
  });
};

export const requiredRules: InputValidationRules = [
  (v: string): string | boolean => (!!v && v.toString().trim().length > 0) || i18n.t('labels.required') as string,
];

/**
 * Cross-field rule: at least one of company or contact must be non-empty.
 * Pass the "other" field's value; returns true if either has content.
 */
export const contactOrCompanyRule = (
  otherFieldValue: string,
): InputValidationRules => [
  (v: string): string | boolean => {
    const hasThis = !!v && v.trim().length > 0;
    const hasOther = !!otherFieldValue && otherFieldValue.trim().length > 0;
    return (hasThis || hasOther)
      || i18n.t('shipment.validationErrors.contactOrCompanyRequired') as string;
  },
];

export const firstAndLastNameRules: InputValidationRules = [
  (v: string): string | boolean => {
    if (!v) return true;

    const names = v.trim().split(' ');

    if (names.length < 2) {
      return i18n.t('labels.requiredFullName') as string;
    }

    return true;
  },
];

export const emailRules: InputValidationRules = [
  (v: string): string | boolean => {
    return v
      ? (!!emailRegEx.test(v))
        || i18n.t('alertDialog.emailMustBeValid') as string
      : true;
  },
];

export const numberRules: InputValidationRules = [
  (v: string): string | boolean => +v > 0 || i18n.t('labels.negativeOrZeroNotAllowed') as string,
];

export const maxNumberRules = (limit: number): InputValidationRules => {
  return [
    (v: string): string | boolean => +v <= limit || i18n.t('labels.invalidLength') as string,
  ];
};

export const notRequiredNumberRules: InputValidationRules = [
  (v: string): string | boolean => +v >= 0 || i18n.t('labels.negativeNotAllowed') as string,
];

export const descriptionVagueRules: InputValidationRules = [
  (v: string): string | boolean => (v && v.toString().trim().length >= 7)
      || i18n.t('shipment.commercialInvoice.validationRules.vagueDescription') as string,
];

export const phoneNumberInvalidRules: InputValidationRules = [
  (v: string): string | boolean => {
    if (!v) return true;

    const val = v.replace(
      /[^0-9]/gm, '',
    );
    return (val && val.toString().trim().length >= 5)
      || i18n.t('labels.phoneNumberInvalid') as string;
  },
];

export const carrierAccountRules = (
  carrierCode: keyof typeof CarrierCodeType,
): InputValidationRules => {
  if (carrierCode === 'UPS') {
    return [
      (v: string): string | boolean => {
        const strVal = v.toString().trim();
        return (strVal.length === 6 && isAlphaNumeric(strVal))
        || i18n.t('shipment.validationErrors.upsAccount') as string;
      },
    ];
  }

  if (carrierCode === 'FedEx') {
    return [
      (v: string): string | boolean => {
        const strVal = v.toString().trim();
        return (strVal.length === 9 && isNumeric(strVal))
        || i18n.t('shipment.validationErrors.fedExAccount') as string;
      },
    ];
  }

  return [];
};

export const creditCardRules = (cardType?: creditCardType): InputValidationRules => {
  return [
    (v: string): string | boolean => {
      if (!v) return true;
      return validateCCWithMessage(v, cardType);
    },
  ];
};

export const cvvRules: InputValidationRules = [
  (v: string): string | boolean => (v ? v.trim().length >= 3
      || i18n.t('labels.required') as string : true),
];

export const hsCodeRules: InputValidationRules = [
  (v: string): string | boolean => {
    if (!v) return true;
    const val = v.replace(/\s/g, '');
    return (val.length >= 6 && val.length <= 15)
      || i18n.t('shipment.validationErrors.hsCodeInvalid') as string;
  },
];

export const isProvinceRequired = (countryCode?: string) => {
  if (!countryCode) return false;
  return safeListedCountryCodes.includes(countryCode);
};
